import { colors } from '@config/theme'
import { Box, IconButton, Typography } from '@mui/material'
import { OverlayView } from '@react-google-maps/api'
import mixpanel from 'mixpanel-browser'
import { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'

interface MapMarkerCardProps {
  onClose: () => void
  selectedPoint: any
  map: google.maps.Map | null
}

const ProviderLinkElement = ({ id, name, testId }: { id: string | number; name: string; testId: string }) => {
  return (
    <StyledLinkText
      data-testid={testId}
      href={`/camps?filter_selected_providers=[${id}]`}
      target="_blank"
      rel="noreferrer"
      onClick={() => {
        mixpanel.track('Clicked Map Marker Card Provider Link', {
          'Map Marker Type': 'Default',
          'Map Marker Provider': `[${id}]`,
        })
      }}
    >
      {name}
    </StyledLinkText>
  )
}

export const MapMarkerCard = ({ onClose, selectedPoint, map }: MapMarkerCardProps) => {
  // Define possible positions for the tooltip
  const [tooltipPosition, setTooltipPosition] = useState<'bottom' | 'top' | 'left' | 'right'>('bottom')
  const tooltipRef = useRef<HTMLDivElement>(null)

  // Check marker position relative to map bounds and adjust tooltip position
  const checkPositionRelativeToBounds = useCallback(() => {
    if (!map) {
      return
    }

    try {
      // Get map bounds
      const bounds = map.getBounds()
      if (!bounds) {
        return
      }

      // Get bounds coordinates
      const ne = bounds.getNorthEast()
      const sw = bounds.getSouthWest()

      const north = ne.lat()
      const east = ne.lng()
      const south = sw.lat()
      const west = sw.lng()

      // Calculate dimensions
      const totalHeight = north - south
      const totalWidth = east - west

      // Calculate distance of marker from edges (as ratio of total size)
      const bottomRatio = (selectedPoint.lat - south) / totalHeight
      const topRatio = (north - selectedPoint.lat) / totalHeight
      const leftRatio = (selectedPoint.lng - west) / totalWidth
      const rightRatio = (east - selectedPoint.lng) / totalWidth

      // Determine the closest edge (smallest ratio means closest to that edge)
      const minRatio = Math.min(bottomRatio, topRatio, leftRatio, rightRatio)

      // Set position based on the edge the marker is closest to
      if (minRatio === bottomRatio && bottomRatio < 0.25) {
        setTooltipPosition('top') // If close to bottom, show tooltip on top
      } else if (minRatio === topRatio && topRatio < 0.32) {
        setTooltipPosition('bottom') // If close to top, show tooltip on bottom
      } else if (minRatio === leftRatio && leftRatio < 0.22) {
        setTooltipPosition('right') // If close to left, show tooltip on right
      } else if (minRatio === rightRatio && rightRatio < 0.22) {
        setTooltipPosition('left') // If close to right, show tooltip on left
      } else {
        // Default position if not near any edge
        setTooltipPosition('bottom')
      }
    } catch (error) {
      console.error('Error calculating tooltip position:', error)
      setTooltipPosition('bottom')
    }
  }, [map, selectedPoint.lat, selectedPoint.lng])

  // Run position check when map or position changes
  useEffect(() => {
    if (!map) {
      return
    }
    checkPositionRelativeToBounds()
  }, [map, checkPositionRelativeToBounds])

  // Update position when map bounds change
  useEffect(() => {
    if (!map) {
      return
    }

    const boundsListener = map.addListener('bounds_changed', checkPositionRelativeToBounds)

    return () => {
      google.maps.event.removeListener(boundsListener)
    }
  }, [map, checkPositionRelativeToBounds])

  // Generate styles based on tooltip position
  const getTooltipStyles = () => {
    switch (tooltipPosition) {
      case 'bottom':
        return {
          transform: 'translateX(-50%)',
          margin: '0 0 0 0',
        }
      case 'top':
        return {
          transform: 'translateX(-50%) translateY(-100%)',
          margin: '-60px 0 0 0',
        }
      case 'left':
        return {
          transform: 'translateY(-100%) translateX(-100%)',
          margin: '0 0 0 -24px',
        }
      case 'right':
        return {
          transform: 'translateY(-100%)',
          margin: '0 0 0 24px',
        }
      default:
        return {
          transform: 'translateX(-50%)',
          margin: '0 0 0 0',
        }
    }
  }

  const tooltipStyles = getTooltipStyles()

  // Utility function to generate popup info for a group of camps at an address
  const getPopupInfo = (camps: any[]) => {
    if (!camps || !Array.isArray(camps)) {
      return { providers: [], campCount: 0, facilities: [] }
    }

    // 1. Providers (Corporate and Program)
    const providerMap = new Map<string, { id: number; name: string }>()

    camps.forEach((camp) => {
      if (camp.corporate_provider && camp.corporate_provider_name) {
        providerMap.set(camp.corporate_provider, {
          id: camp.corporate_provider,
          name: camp.corporate_provider_name,
        })
      }
      if (camp.program_provider && camp.program_provider_name) {
        providerMap.set(camp.program_provider, {
          id: camp.program_provider,
          name: camp.program_provider_name,
        })
      }
    })

    const providers = Array.from(providerMap.values()).sort((a, b) => a.name.localeCompare(b.name))

    // 2. Camp count and offer(s)/camp(s)
    const campCount = camps.length

    // 3. Facilities
    const facilities = Array.from(new Set(camps.map((camp) => camp.facility_name).filter(Boolean))).sort((a, b) =>
      a.localeCompare(b)
    )

    return { providers, campCount, facilities }
  }

  // Generate popup info from camps data
  const popupInfo = selectedPoint?.camps ? getPopupInfo(selectedPoint.camps) : null

  const popupBox = (
    <Box
      ref={tooltipRef}
      sx={{
        backgroundColor: '#fff',
        padding: '16px',
        borderRadius: '12px',
        border: '1px solid #D88EC8',
        boxShadow: '0px 24px 48px -12px rgba(16, 24, 40, 0.38);',
        width: '350px',
        position: 'relative',
        fontFamily: 'Inter, sans-serif',
        // Only apply positioning transforms when there's a map
        ...(map && {
          transform: tooltipStyles.transform,
          margin: tooltipStyles.margin,
        }),
        // Center the popup in Storybook when no map
        ...(!map && {
          margin: '20px auto',
          transform: 'none',
        }),
      }}
      data-testid={`map-marker-card-address-${selectedPoint.id}`}
    >
      <IconButton
        size="small"
        onClick={onClose}
        sx={{
          position: 'absolute',
          top: '14px',
          right: '8px',
          color: colors.brandGray700,
          padding: '2px',
        }}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="21" height="21" viewBox="0 0 21 21" fill="none">
          <path
            d="M15.5469 5.17413L5.54688 15.1741M5.54688 5.17413L15.5469 15.1741"
            stroke="#344054"
            strokeWidth="1.66667"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </IconButton>
      {popupInfo ? (
        <StyledPopupContent>
          {/* Providers */}
          <StyledPopupTitle data-testid={`map-marker-card-providers-address-${selectedPoint.id}`}>
            {(() => {
              const providers = popupInfo.providers
              if (providers.length > 4) {
                return (
                  <ProviderLinkElement
                    name={'More than 4 providers'}
                    id={providers.map((p: { id: number; name: string }) => p.id).join(',')}
                    testId={`map-marker-card-providers-address-${selectedPoint.id}-index-1`}
                  />
                )
              } else if (providers.length === 1) {
                return (
                  <ProviderLinkElement
                    name={providers[0].name}
                    id={providers[0].id}
                    testId={`map-marker-card-providers-address-${selectedPoint.id}-index-1`}
                  />
                )
              } else if (providers.length === 2) {
                return (
                  <span>
                    <ProviderLinkElement
                      name={providers[0].name}
                      id={providers[0].id}
                      testId={`map-marker-card-providers-address-${selectedPoint.id}-index-1`}
                    />{' '}
                    and{' '}
                    <ProviderLinkElement
                      name={providers[1].name}
                      id={providers[1].id}
                      testId={`map-marker-card-providers-address-${selectedPoint.id}-index-2`}
                    />
                  </span>
                )
              } else if (providers.length > 2) {
                return (
                  <span>
                    {providers.slice(0, -1).map((p, idx) => (
                      <span key={p.id}>
                        <ProviderLinkElement
                          name={p.name}
                          id={p.id}
                          testId={`map-marker-card-providers-address-${selectedPoint.id}-index-${idx + 1}`}
                        />
                        {idx < providers.length - 2 ? ', ' : ', and '}
                      </span>
                    ))}
                    <ProviderLinkElement
                      name={providers[providers.length - 1].name}
                      id={providers[providers.length - 1].id}
                      testId={`map-marker-card-providers-address-${selectedPoint.id}-index-${providers.length}`}
                    />
                  </span>
                )
              } else {
                return ''
              }
            })()}
          </StyledPopupTitle>
          {/* Camps */}
          <StyledPopupText data-testid={`map-marker-card-camps-address-${selectedPoint.id}`}>
            {(() => {
              const campCount = popupInfo.campCount
              const providerCount = popupInfo.providers.length
              const offerWord = providerCount > 1 ? 'offer' : 'offers'
              const campWord = campCount > 1 ? 'camps' : 'camp'
              return `${offerWord} ${campCount} ${campWord}`
            })()}
          </StyledPopupText>
          {/* Facilities */}
          <StyledPopupText data-testid={`map-marker-card-facilities-address-${selectedPoint.id}`}>
            {(() => {
              const facilities = popupInfo.facilities
              if (facilities.length > 4) {
                return `at ${facilities[0]} and more`
              } else if (facilities.length === 1) {
                return `at ${facilities[0]}`
              } else if (facilities.length === 2) {
                return `at ${facilities[0]} and ${facilities[1]}`
              } else if (facilities.length > 2) {
                return 'at ' + facilities.slice(0, -1).join(', ') + ', and ' + facilities[facilities.length - 1]
              } else {
                return ''
              }
            })()}
          </StyledPopupText>
        </StyledPopupContent>
      ) : (
        <div>Popup content here.</div>
      )}
    </Box>
  )

  // If no map is provided (like in Storybook), render the popup directly
  if (!map) {
    return popupBox
  }

  // Otherwise, render it as an overlay on the map
  return (
    <OverlayView
      position={{ lat: selectedPoint.lat, lng: selectedPoint.lng }}
      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
    >
      {popupBox}
    </OverlayView>
  )
}

const StyledPopupContent = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const StyledPopupTitle = styled(Typography)`
  width: 93%;
  color: ${colors.brandGray900};
  font-size: 1rem !important;
  line-height: 1.25rem !important;
  font-weight: 600 !important;
`

const StyledPopupText = styled(Typography)`
  color: ${colors.brandGray700};
  font-size: 0.875rem !important;
  line-height: 1.25rem !important;
  font-weight: 400 !important;
`

const StyledLinkText = styled.a`
  text-decoration-line: underline;
  text-decoration-style: solid;
  text-decoration-skip-ink: none;
  text-decoration-thickness: auto;
  text-underline-offset: auto;
  text-underline-position: from-font;
`
